import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";

// Captures the speedometer from a live camera stream rather than a file input.
//
// A file input with capture="environment" is only a hint: several Android builds still expose a
// gallery path from that picker, and desktop ignores it outright. Drawing a frame from
// getUserMedia to a canvas means there is no picker at all, so a stored image cannot be submitted.
const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.7;

const CAMERA_COPY = {
  denied: {
    title: "Camera is blocked for this site",
    body: "Your browser is blocking the camera for iotrips.in. Tap the icon to the left of the address bar, open Permissions, then set Camera to Allow.",
  },
  unavailable: {
    title: "Camera not available",
    body: "No camera could be started on this device. Close any other app using the camera and try again.",
  },
  unsupported: {
    title: "Camera not supported",
    body: "This browser cannot open the camera. Open the trip link in Chrome to continue.",
  },
};

export default function OdometerCaptureModal({ show, eventLabel, onCancel, onConfirm }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraState, setCameraState] = useState("starting");
  const [shot, setShot] = useState(null);
  const [reading, setReading] = useState("");
  const [saving, setSaving] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraState("unsupported");
      return;
    }
    setCameraState("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // iOS will not start a stream that is not muted and playsInline.
        await videoRef.current.play().catch(() => null);
      }
      setCameraState("ready");
    } catch (error) {
      setCameraState(error?.name === "NotAllowedError" ? "denied" : "unavailable");
    }
  }, []);

  useEffect(() => {
    if (!show) {
      stopStream();
      setShot(null);
      setReading("");
      setSaving(false);
      return undefined;
    }
    startCamera();
    return stopStream;
  }, [show, startCamera, stopStream]);

  useEffect(() => () => stopStream(), [stopStream]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    // A raw phone frame is 3-8 MB. Drivers are on mobile data in the hills, so downscale before
    // it ever reaches the queue or the network.
    const scale = Math.min(1, MAX_EDGE / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        setShot({ blob, url: URL.createObjectURL(blob) });
        stopStream();
      },
      "image/jpeg",
      JPEG_QUALITY,
    );
  };

  const retake = () => {
    if (shot?.url) URL.revokeObjectURL(shot.url);
    setShot(null);
    startCamera();
  };

  const confirm = async () => {
    if (!shot?.blob) return;
    setSaving(true);
    try {
      await onConfirm({ blob: shot.blob, reading: Number(reading) });
    } finally {
      setSaving(false);
    }
  };

  const readingValid = /^\d{1,7}$/.test(reading.trim());
  const problem = CAMERA_COPY[cameraState];

  return (
    <Modal show={show} centered backdrop="static" keyboard={false} onHide={onCancel}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title className="h6 mb-0">Speedometer photo - {eventLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {problem ? (
          <div className="text-center py-3">
            <div className="fw-semibold mb-2">{problem.title}</div>
            <div className="small text-muted mb-3">{problem.body}</div>
            <Button variant="outline-primary" onClick={startCamera}>Retry</Button>
          </div>
        ) : (
          <>
            <div className="ratio ratio-4x3 bg-dark rounded overflow-hidden mb-3">
              {shot ? (
                <img src={shot.url} alt="Speedometer" style={{ objectFit: "contain" }} />
              ) : (
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  autoPlay
                  style={{ objectFit: "cover", width: "100%", height: "100%" }}
                />
              )}
            </div>
            {cameraState === "starting" && !shot ? (
              <div className="text-center small text-muted mb-2">
                <Spinner size="sm" className="me-2" />Starting camera...
              </div>
            ) : null}
            {shot ? (
              <Form.Group className="mb-2">
                <Form.Label className="small fw-semibold mb-1">Odometer reading (km)</Form.Label>
                <Form.Control
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={reading}
                  onChange={(event) => setReading(event.target.value)}
                  placeholder="e.g. 48213"
                  disabled={saving}
                />
                <div className="small text-muted mt-1">
                  Type the number shown in the photo. Start and end readings are compared later.
                </div>
              </Form.Group>
            ) : (
              <div className="small text-muted text-center">
                Point the rear camera at the speedometer and keep the numbers readable.
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="link" className="text-decoration-none" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <div className="d-flex gap-2">
          {shot ? (
            <>
              <Button variant="outline-secondary" onClick={retake} disabled={saving}>Retake</Button>
              <Button variant="success" onClick={confirm} disabled={!readingValid || saving}>
                {saving ? "Saving..." : "Confirm"}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={capture} disabled={cameraState !== "ready"}>
              Capture photo
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
